// src/services/AuditService.js
import storage from './StorageAdapter';
import { t, getCurrentLanguage } from '../useTranslations';
import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';

class AuditService {
  constructor() {
    this.storageKey = 'rms_audit_logs';
    this.maxLogs = 1000; // Begränsa antal logs i localStorage
    this.retentionDays = 30; // Behåll logs i 30 dagar
    this.listeners = [];
  }

  // Hämta aktuell användare från aktiv session
  getCurrentUser() {
    try {
      const session = storage.getJSON('rms_session', null);
      if (session && session.authenticated) {
        return {
          id: session.username,
          username: session.username,
          role: session.role || 'user'
        };
      }
    } catch (error) {
      // fall through to default
    }
    return {
      id: 'unknown',
      username: 'unknown',
      role: 'unknown'
    };
  }

  // Logga en händelse
  async log(action, module, details = {}, level = 'INFO') {
    try {
      const logEntry = {
        id: this.generateId(),
        timestamp: new Date().toISOString(),
        user: this.getCurrentUser(),
        action,
        module,
        details,
        level, // INFO, WARNING, ERROR
        session: this.getSessionInfo()
      };

      await this.saveLog(logEntry);
      await this.cleanupOldLogs();
    } catch (error) {
      console.error('[AUDIT] Error logging event:', error);
    }
  }

  // Specifika log-metoder för vanliga åtgärder
  async logCreate(module, entityType, entityId, data) {
    this.log('CREATE', module, {
      entityType,
      entityId,
      data
    });
  }

  async logUpdate(module, entityType, entityId, changes, comment = '') {
    this.log('UPDATE', module, {
      entityType,
      entityId,
      changes,
      comment
    });
  }

  async logDelete(module, entityType, entityId) {
    this.log('DELETE', module, {
      entityType,
      entityId
    }, 'WARNING');
  }

  async logLogin(success = true) {
    this.log('LOGIN', 'Auth', {
      success,
      timestamp: new Date().toISOString()
    });
  }

  async logLogout() {
    this.log('LOGOUT', 'Auth', {
      timestamp: new Date().toISOString()
    });
  }

  async logDataImport(module, fileName, recordCount, success = true) {
    this.log('DATA_IMPORT', module, {
      fileName,
      recordCount,
      success
    });
  }

  async logExport(module, exportType, recordCount) {
    this.log('DATA_EXPORT', module, {
      exportType,
      recordCount
    });
  }

  async logAction(module, entityType, action, details = {}, comment = '') {
    this.log(action, module, { entityType, ...details, comment });
  }

  // Hämta alla logs
  getLogs(filters = {}) {
    const logs = this.getAllLogs();
    return this.filterLogs(logs, filters);
  }

  // Filtrera logs baserat på kriterier
  filterLogs(logs, filters) {
    let filteredLogs = [...logs];

    if (filters.module && typeof filters.module === 'string') {
      const moduleTerm = filters.module.toLowerCase();
      filteredLogs = filteredLogs.filter(log =>
        (log.module || '').toLowerCase().includes(moduleTerm)
      );
    }

    if (filters.action && typeof filters.action === 'string') {
      const actionTerm = filters.action.toLowerCase();
      filteredLogs = filteredLogs.filter(log => {
        const action = typeof log.action === 'string' ? log.action : '';
        return action.toLowerCase().includes(actionTerm);
      });
    }

    if (filters.user && typeof filters.user === 'string') {
      const userTerm = filters.user.toLowerCase();
      filteredLogs = filteredLogs.filter(log =>
        (log.user?.username || '').toLowerCase().includes(userTerm)
      );
    }

    if (filters.level) {
      filteredLogs = filteredLogs.filter(log => log.level === filters.level);
    }

    if (filters.dateFrom) {
      filteredLogs = filteredLogs.filter(log => 
        new Date(log.timestamp) >= new Date(filters.dateFrom)
      );
    }

    if (filters.dateTo) {
      const toDate = new Date(filters.dateTo);
      toDate.setHours(23, 59, 59, 999);
      filteredLogs = filteredLogs.filter(log =>
        new Date(log.timestamp) <= toDate
      );
    }

    return filteredLogs.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
  }

  // Exportera logs till CSV – returnerar antal exporterade poster
  exportToCsv(filters = {}) {
    const logs = this.getLogs(filters);
    const headers = [
      t('modules.administration.audit.timestamp'),
      t('modules.administration.audit.user'),
      t('modules.administration.audit.module'),
      t('modules.administration.audit.action'),
      t('modules.administration.audit.level'),
      t('modules.administration.audit.details')
    ];

    const csvField = (val) => `"${String(val).replace(/"/g, '""')}"`;
    const formatTs = (ts) => {
      try { return new Date(ts).toLocaleString('sv-SE'); } catch { return ts; }
    };
    const csvContent = [
      headers.map(csvField).join(','),
      ...logs.map(log => [
        formatTs(log.timestamp),
        log.user?.username || t('common.unknown'),
        log.module || t('common.unknown'),
        typeof log.action === 'string' ? log.action : t('common.unknown'),
        log.level || t('modules.administration.audit.levelDefault'),
        JSON.stringify(log.details || {})
      ].map(csvField).join(','))
    ].join('\n');

    // UTF-8 BOM ensures Swedish characters render correctly in Excel
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `audit-logs-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    return logs.length;
  }

  // Exportera logs till Excel (.xlsx) – returnerar antal exporterade poster
  exportToExcel(filters = {}) {
    const logs = this.getLogs(filters);
    const formatTs = (ts) => {
      try { return new Date(ts).toLocaleString('sv-SE'); } catch { return ts; }
    };

    const headers = [
      t('modules.administration.audit.timestamp'),
      t('modules.administration.audit.user'),
      t('modules.administration.audit.module'),
      t('modules.administration.audit.action'),
      t('modules.administration.audit.level'),
      t('modules.administration.audit.details')
    ];

    const rows = logs.map(log => [
      formatTs(log.timestamp),
      log.user?.username || t('common.unknown'),
      log.module || t('common.unknown'),
      typeof log.action === 'string' ? log.action : t('common.unknown'),
      log.level || 'INFO',
      JSON.stringify(log.details || {})
    ]);

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    ws['!cols'] = [
      { wch: 22 },
      { wch: 18 },
      { wch: 18 },
      { wch: 16 },
      { wch: 10 },
      { wch: 60 },
    ];
    const sheetName = t('modules.administration.audit.title').substring(0, 31);
    XLSX.utils.book_append_sheet(wb, ws, sheetName);
    XLSX.writeFile(wb, `audit-logs-${new Date().toISOString().split('T')[0]}.xlsx`);
    return logs.length;
  }

  // Exportera logs till PDF – returnerar antal exporterade poster
  exportToPdf(filters = {}) {
    const logs = this.getLogs(filters);
    const lang = getCurrentLanguage() || 'sv';
    const locale = lang === 'sv' ? 'sv-SE' : 'en-GB';
    const title = t('modules.administration.audit.title');

    const doc = new jsPDF('l', 'mm', 'a4'); // landscape A4
    const PAGE_W = 297;
    const PAGE_H = 210;
    const MARGIN = 14;
    const CONTENT_W = PAGE_W - MARGIN * 2;
    const FOOTER_Y = PAGE_H - 7;

    const clrPrimary  = [26,  58,  92];
    const clrAccent   = [41, 128, 185];
    const clrLight    = [245, 248, 252];
    const clrDarkText = [30,  40,  50];
    const clrMuted    = [110, 120, 135];
    const clrBorder   = [210, 218, 228];

    const formatTs = (ts) => {
      try { return new Date(ts).toLocaleString(locale); } catch { return ts; }
    };

    // Column definitions (total = CONTENT_W = 269mm)
    const cols = [
      { label: t('modules.administration.audit.time'),    width: 42 },
      { label: t('modules.administration.audit.user'),    width: 28 },
      { label: t('modules.administration.audit.module'),  width: 28 },
      { label: t('modules.administration.audit.action'),  width: 25 },
      { label: t('modules.administration.audit.level'),   width: 20 },
      { label: t('modules.administration.audit.details'), width: CONTENT_W - 143 },
    ];

    const ROW_H    = 7;
    const HEADER_H = 9;

    const renderPageHeader = () => {
      doc.setFillColor(...clrPrimary);
      doc.rect(0, 0, PAGE_W, 19, 'F');
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(255, 255, 255);
      doc.text(`Prime RMS — ${title}`, MARGIN, 12.5);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.text(formatTs(new Date().toISOString()), PAGE_W - MARGIN, 12.5, { align: 'right' });
    };

    const renderTableHeader = (yPos) => {
      doc.setFillColor(...clrAccent);
      doc.rect(MARGIN, yPos, CONTENT_W, HEADER_H, 'F');
      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(255, 255, 255);
      let x = MARGIN + 2;
      for (const col of cols) {
        doc.text(col.label, x, yPos + 6);
        x += col.width;
      }
      return yPos + HEADER_H;
    };

    const renderFooter = (pageNum, totalPages) => {
      doc.setDrawColor(...clrBorder);
      doc.setLineWidth(0.3);
      doc.line(MARGIN, FOOTER_Y - 3, PAGE_W - MARGIN, FOOTER_Y - 3);
      doc.setFontSize(7);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...clrMuted);
      doc.text(`Prime RMS  |  ${title}`, MARGIN, FOOTER_Y);
      doc.text(`${pageNum} / ${totalPages}`, PAGE_W - MARGIN, FOOTER_Y, { align: 'right' });
    };

    renderPageHeader();
    let y = renderTableHeader(22);

    let rowIdx = 0;
    for (const log of logs) {
      if (y + ROW_H > FOOTER_Y - 5) {
        doc.addPage();
        renderPageHeader();
        y = renderTableHeader(22);
        rowIdx = 0;
      }

      if (rowIdx % 2 === 0) {
        doc.setFillColor(...clrLight);
        doc.rect(MARGIN, y, CONTENT_W, ROW_H, 'F');
      }

      doc.setDrawColor(...clrBorder);
      doc.setLineWidth(0.1);
      doc.line(MARGIN, y + ROW_H, MARGIN + CONTENT_W, y + ROW_H);

      doc.setFontSize(7.5);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...clrDarkText);

      const detailsStr = JSON.stringify(log.details || {});
      const detailsTrunc = detailsStr.length > 120 ? detailsStr.substring(0, 120) + '…' : detailsStr;
      const vals = [
        formatTs(log.timestamp),
        log.user?.username || '—',
        log.module || '—',
        typeof log.action === 'string' ? log.action : '—',
        log.level || 'INFO',
        detailsTrunc,
      ];

      let x = MARGIN + 2;
      for (let i = 0; i < cols.length; i++) {
        const lines = doc.splitTextToSize(String(vals[i] || ''), cols[i].width - 3);
        doc.text(lines[0] || '', x, y + 4.8);
        x += cols[i].width;
      }

      y += ROW_H;
      rowIdx++;
    }

    if (logs.length === 0) {
      doc.setFontSize(10);
      doc.setTextColor(...clrMuted);
      doc.setFont('helvetica', 'italic');
      doc.text(t('modules.administration.audit.noLogs'), MARGIN + 4, y + 8);
    }

    const totalPages = doc.getNumberOfPages();
    for (let p = 1; p <= totalPages; p++) {
      doc.setPage(p);
      renderFooter(p, totalPages);
    }

    doc.save(`audit-logs-${new Date().toISOString().split('T')[0]}.pdf`);
    return logs.length;
  }

  // Read audit settings from SettingsService storage (avoids circular import)
  getAuditSettings() {
    const settings = storage.getJSON('rms_settings', {});
    return {
      retentionDays: settings.audit?.retentionDays ?? this.retentionDays,
      maxLogs: settings.audit?.maxLogs ?? this.maxLogs
    };
  }

  // Rensa gamla logs
  async cleanupOldLogs() {
    try {
      const logs = this.getAllLogs();
      const { retentionDays, maxLogs } = this.getAuditSettings();
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - retentionDays);

      const validLogs = logs.filter(log =>
        new Date(log.timestamp) > cutoffDate
      );

      // Behåll endast de senaste logs om det finns för många
      const trimmedLogs = validLogs.slice(0, maxLogs);

      await storage.setJSON(this.storageKey, trimmedLogs);
    } catch (error) {
      console.error('[AUDIT] Error cleaning up logs:', error);
    }
  }

  // Privata hjälpmetoder
  getAllLogs() {
    return storage.getJSON(this.storageKey, []);
  }

  async saveLog(logEntry) {
    try {
      const logs = this.getAllLogs();
      // Hash chain: prevHash is the most recent hashed entry's hash
      const prevHash = (logs.length > 0 && logs[0].hash) ? logs[0].hash : '00000000';
      const hash = this.hashEntry(logEntry, prevHash);
      const entryWithHash = { ...logEntry, prevHash, hash };
      logs.unshift(entryWithHash); // Lägg till längst upp (senaste först)
      await storage.setJSON(this.storageKey, logs);
      this.notifyListeners();
    } catch (error) {
      console.error('[AUDIT] Error saving log:', error);
    }
  }

  // === CHANGE LISTENERS ===

  subscribe(listener) {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  notifyListeners() {
    this.listeners.forEach(listener => listener());
  }

  generateId() {
    return Date.now().toString(36) + Math.random().toString(36).substr(2, 9);
  }

  // Compute a deterministic hash of arbitrary data (djb2 variant)
  computeHash(data) {
    const str = typeof data === 'string' ? data : JSON.stringify(data);
    let hash = 5381;
    for (let i = 0; i < str.length; i++) {
      hash = ((hash << 5) + hash + str.charCodeAt(i)) & 0xffffffff;
    }
    return (hash >>> 0).toString(16).padStart(8, '0');
  }

  // Hash a log entry combined with the previous hash (chain link)
  hashEntry(entry, prevHash) {
    const canonical = {
      id: entry.id,
      timestamp: entry.timestamp,
      action: entry.action,
      module: entry.module,
      level: entry.level,
      user: entry.user,
      details: entry.details,
      prevHash
    };
    return this.computeHash(canonical);
  }

  // Validate the integrity of the hash chain.
  // Returns { valid: boolean, brokenAt: string|null }
  // Only validates entries that carry a hash field (post-INFRA-071 entries).
  // Uses chain-following (not timestamp sort) so same-millisecond entries work correctly.
  validateChain() {
    const logs = this.getAllLogs();
    const hashed = logs.filter(e => e.hash);

    if (hashed.length === 0) return { valid: true, brokenAt: null };

    const hashSet = new Set(hashed.map(e => e.hash));
    // Map prevHash → entry (used to walk forward through the chain)
    const byPrevHash = new Map(hashed.map(e => [e.prevHash, e]));

    // Genesis: the entry whose prevHash is not any other entry's hash.
    // After normal cleanup (removes oldest), exactly one genesis exists.
    const genesis = hashed.find(e => !hashSet.has(e.prevHash));
    if (!genesis) {
      // Circular or no clear start — chain corrupted
      return { valid: false, brokenAt: hashed[0].id };
    }

    // Validate genesis self-consistency
    if (this.hashEntry(genesis, genesis.prevHash) !== genesis.hash) {
      return { valid: false, brokenAt: genesis.id };
    }

    // Follow the chain forward from genesis, verifying each link
    const visited = new Set([genesis.id]);
    let current = genesis;

    while (true) {
      const next = byPrevHash.get(current.hash);
      if (!next) break; // End of chain

      if (visited.has(next.id)) {
        return { valid: false, brokenAt: next.id }; // Cycle detected
      }

      const computed = this.hashEntry(next, next.prevHash);
      if (computed !== next.hash || next.prevHash !== current.hash) {
        return { valid: false, brokenAt: next.id };
      }

      visited.add(next.id);
      current = next;
    }

    // Every hashed entry must be reachable from genesis (detects deleted middle entries)
    if (visited.size !== hashed.length) {
      const orphan = hashed.find(e => !visited.has(e.id));
      return { valid: false, brokenAt: orphan ? orphan.id : null };
    }

    return { valid: true, brokenAt: null };
  }

  getSessionInfo() {
    return {
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
      url: window.location.href
    };
  }

  // Rensa alla logs — kräver admin-behörighet (verifieras i UI innan anrop)
  async clearAllLogs() {
    try {
      const logs = this.getAllLogs();
      const count = logs.length;
      // Rensa lagringen helt
      storage.removeItem(this.storageKey);
      // Logga rensningshändelsen som ny genesis i hash-kedjan
      await this.log('AUDIT_CLEAR', 'Admin', {
        entriesCleared: count,
        clearedAt: new Date().toISOString()
      }, 'WARNING');
      return count;
    } catch (error) {
      console.error('[AUDIT] Error clearing logs:', error);
      return 0;
    }
  }
}

// Singleton instance
const auditService = new AuditService();

export default auditService;
